import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Users, GraduationCap, Briefcase, TrendingUp, FileText, ExternalLink } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { hoverLift, springPresets } from "@/lib/motion";
import type { Department, NewsItem, FacultyMember, Event } from "@/lib/index";
import { useEffect, useState } from "react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Code: () => <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  Network: () => <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="16" y="16" width="6" height="6" rx="1"/><rect x="2" y="16" width="6" height="6" rx="1"/><rect x="9" y="2" width="6" height="6" rx="1"/><path d="M12 8v8"/><path d="M8 19H5"/><path d="M19 19h-3"/></svg>,
  Briefcase: Briefcase,
  Calculator: () => <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="16" y1="10" x2="16" y2="10.01"/><line x1="12" y1="10" x2="12" y2="10.01"/><line x1="8" y1="10" x2="8" y2="10.01"/><line x1="16" y1="14" x2="16" y2="14.01"/><line x1="12" y1="14" x2="12" y2="14.01"/><line x1="8" y1="14" x2="8" y2="14.01"/><line x1="16" y1="18" x2="16" y2="18.01"/><line x1="12" y1="18" x2="12" y2="18.01"/><line x1="8" y1="18" x2="8" y2="18.01"/></svg>,
  TrendingUp: TrendingUp,
  Scale: () => <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v18"/><path d="M5 7l7-4 7 4"/><path d="M5 7v6l7 4"/><path d="M19 7v6l-7 4"/></svg>,
};

export function DepartmentCard({ department }: { department: Department }) {
  const IconComponent = iconMap[department.icon];

  return (
    <motion.div variants={hoverLift} initial="rest" whileHover="hover">
      <Card className="h-full border-2 hover:border-primary/50 transition-colors duration-200">
        <CardHeader>
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground mb-4 shadow-lg">
            {IconComponent && <IconComponent className="w-8 h-8" />}
          </div>
          <CardTitle className="text-2xl font-bold text-right">{department.nameAr}</CardTitle>
          <CardDescription className="text-right text-base">{department.nameEn}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-end gap-2 text-muted-foreground">
            <span className="font-medium">{department.studentCount}+</span>
            <Users className="w-5 h-5" />
            <span>طالب</span>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-right">المستويات الدراسية:</p>
            <div className="flex flex-wrap gap-2 justify-end">
              {department.degreeLevels.map((level, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {level}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button variant="outline" className="w-full" asChild>
            <a href={`#departments`}>
              <span>التفاصيل</span>
              <ExternalLink className="w-4 h-4 mr-2" />
            </a>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export function NewsCard({ news }: { news: NewsItem }) {
  const categoryColors: Record<NewsItem['category'], string> = {
    'أخبار الكلية': 'bg-primary text-primary-foreground',
    'إنجازات الطلاب': 'bg-accent text-accent-foreground',
    'فعاليات': 'bg-chart-3 text-white',
    'إعلانات': 'bg-chart-5 text-white',
  };

  return (
    <motion.div variants={hoverLift} initial="rest" whileHover="hover">
      <Card className="h-full overflow-hidden border-2 hover:border-primary/50 transition-colors duration-200">
        <div className="aspect-video overflow-hidden">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <Badge className={categoryColors[news.category]}>{news.category}</Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <span>{new Date(news.date).toLocaleDateString('ar-LY')}</span>
              <Calendar className="w-4 h-4" />
            </div>
          </div>
          <CardTitle className="text-xl font-bold text-right line-clamp-2">{news.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-right line-clamp-3">{news.excerpt}</p>
        </CardContent>
        <CardFooter>
          <Button variant="ghost" className="w-full" asChild>
            <a href="#news">
              <span>اقرأ المزيد</span>
              <ExternalLink className="w-4 h-4 mr-2" />
            </a>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export function FacultyCard({ member }: { member: FacultyMember }) {
  return (
    <motion.div variants={hoverLift} initial="rest" whileHover="hover">
      <Card className="h-full border-2 hover:border-primary/50 transition-colors duration-200">
        <CardHeader className="text-center">
          <Avatar className="w-24 h-24 mx-auto mb-4 ring-4 ring-primary/20">
            <AvatarImage src={member.avatar} alt={member.name} />
            <AvatarFallback>{member.name.split(' ')[1]?.charAt(0) || 'د'}</AvatarFallback>
          </Avatar>
          <CardTitle className="text-xl font-bold">{member.name}</CardTitle>
          <CardDescription className="text-base">{member.title}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-right">
          <div className="flex items-center justify-end gap-2 text-sm">
            <span className="text-muted-foreground">{member.specialization}</span>
            <GraduationCap className="w-4 h-4 text-primary" />
          </div>
          <div className="flex items-center justify-end gap-2 text-sm">
            <span className="text-muted-foreground">{member.department}</span>
            <Briefcase className="w-4 h-4 text-primary" />
          </div>
          <div className="flex items-center justify-end gap-2 text-sm font-medium">
            <span className="text-accent">{member.experience} سنوات خبرة</span>
            <TrendingUp className="w-4 h-4 text-accent" />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function StatCard({ label, value, icon: Icon }: { label: string; value: number; icon: React.ComponentType<{ className?: string }> }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={springPresets.bouncy}
    >
      <Card className="text-center border-2 hover:border-accent/50 transition-colors duration-200">
        <CardContent className="pt-6 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center text-accent-foreground shadow-lg">
            <Icon className="w-8 h-8" />
          </div>
          <div>
            <p className="text-4xl font-bold text-accent mb-1">{count.toLocaleString('ar-LY')}+</p>
            <p className="text-muted-foreground font-medium">{label}</p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function EventCard({ event }: { event: Event }) {
  const typeColors: Record<Event['type'], string> = {
    'معرض': 'bg-chart-1 text-white',
    'محاضرة': 'bg-chart-2 text-white',
    'توظيف': 'bg-accent text-accent-foreground',
    'ورشة عمل': 'bg-chart-4 text-white',
  };

  return (
    <motion.div variants={hoverLift} initial="rest" whileHover="hover">
      <Card className="border-2 hover:border-primary/50 transition-colors duration-200">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <Badge className={typeColors[event.type]}>{event.type}</Badge>
          </div>
          <CardTitle className="text-lg font-bold text-right">{event.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
            <span>{new Date(event.date).toLocaleDateString('ar-LY')}</span>
            <Calendar className="w-4 h-4" />
          </div>
          <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
            <span>{event.time}</span>
            <Clock className="w-4 h-4" />
          </div>
          <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
            <span>{event.location}</span>
            <MapPin className="w-4 h-4" />
          </div>
        </CardContent>
        <CardFooter>
          <Button variant="outline" className="w-full">
            <span>التفاصيل</span>
            <ExternalLink className="w-4 h-4 mr-2" />
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export function RegulationCard({ regulation }: { regulation: { id: string; title: string; type: string; pages: number; year: number; summary: string } }) {
  const typeColors: Record<string, string> = {
    'لائحة': 'bg-primary text-primary-foreground',
    'دليل': 'bg-accent text-accent-foreground',
    'نموذج': 'bg-chart-3 text-white',
  };

  return (
    <motion.div variants={hoverLift} initial="rest" whileHover="hover">
      <Card className="h-full border-2 hover:border-primary/50 transition-colors duration-200">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{regulation.year}</Badge>
              <Badge variant="outline">{regulation.pages} صفحة</Badge>
            </div>
            <Badge className={typeColors[regulation.type] || 'bg-secondary'}>{regulation.type}</Badge>
          </div>
          <CardTitle className="text-xl font-bold text-right">{regulation.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-right text-sm">{regulation.summary}</p>
        </CardContent>
        <CardFooter className="gap-2">
          <Button variant="outline" className="flex-1">
            <span>عرض</span>
            <FileText className="w-4 h-4 mr-2" />
          </Button>
          <Button className="flex-1">
            <span>تحميل</span>
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}